import type { NavigationGuard } from 'vue-router';
import { until } from '@vueuse/core';
import store from '@/store';
import { useReassessmentStatus } from '@/modules/assessment/api';

// If current org has not completed scoping yet, redirect to 'assessment/scoping',
// else continue to requested route
export const requireScope: NavigationGuard = async (_to, _from, next) => {
  if (store.getters['assessment/isScopingComplete'] === undefined) {
    await store.dispatch('assessment/getAssessmentStatus');
  }

  if (!store.getters['assessment/isScopingComplete']) {
    next({ name: 'scoping' });
  }
  next();
};

// If current org has initial assessment completed then redirect to assessment overview,
// else continue to requested route
export const requireInitialAssessmentNotComplete: NavigationGuard = async (_to, _from, next) => {
  if (store.getters['assessment/isAssessmentComplete'] === undefined) {
    await store.dispatch('assessment/getAssessmentStatus');
  }

  if (store.getters['assessment/isAssessmentComplete']) {
    next({ name: 'assessment' });
    return;
  }
  next();
};

// If current org has a (re)assessment in progress then redirect to the assessment wizard,
// else continue to requested route
export const requireNotAssessing: NavigationGuard = async (to, _from, next) => {
  const { isReassessing, isLoading: isReassessmentLoading } = useReassessmentStatus();

  if (store.getters['assessment/isAssessmentComplete'] === undefined) {
    await store.dispatch('assessment/getAssessmentStatus');
  }

  if (isReassessing.value === undefined) {
    await until(isReassessing).toMatch((c) => typeof c !== 'undefined');
  }

  if (
    (!store.getters['assessment/isAssessmentComplete'] || isReassessing.value) &&
    // Wait for the reassessment status to be loaded before we redirect
    !isReassessmentLoading.value
  ) {
    next({ name: 'wizard', params: to.params, query: to.query });
    return;
  }
  next();
};

// If current org has no (re)assessment in progress then redirect to the assessment overview,
// else continue to requested route
export const requireAssessing: NavigationGuard = async (_to, _from, next) => {
  const { isReassessing, isLoading: isReassessmentLoading } = useReassessmentStatus();

  if (store.getters['assessment/isAssessmentComplete'] === undefined) {
    await store.dispatch('assessment/getAssessmentStatus');
  }

  if (isReassessing.value === undefined) {
    await until(isReassessing).toMatch((c) => typeof c !== 'undefined');
  }

  if (
    store.getters['assessment/isAssessmentComplete'] &&
    !isReassessing.value &&
    // Wait for the reassessment status to be loaded before we redirect
    !isReassessmentLoading.value
  ) {
    next({ name: 'assessment' });
    return;
  }
  next();
};
